import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerAvatarPath} from '@github-ui/paths'
import {useAnalytics} from '@github-ui/use-analytics'
import {useHoverPopover} from '@github-ui/use-hover-popover'
import {AnchoredOverlay, Button, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useId, useRef} from 'react'

import {
  constructReactionButtonDescription,
  constructReactionButtonLabel,
  EMOJI_MAP,
  type ReactionContent,
  type ReactionViewerGroup,
  type Reactor,
  summarizeReactors,
} from '../utils/ReactionGroups'
import styles from './ReactionButton.module.css'

type ReactionButtonProps = {
  reactionGroup: ReactionViewerGroup
  disabled?: boolean
  showReactors?: boolean
  onReact: (reaction: ReactionContent, viewerHasReacted: boolean) => void
}

function ReactorListItem({reactor}: {reactor: Reactor}) {
  // avatarUrl is absent on page-data-driven surfaces (e.g. PR Overview), which don't select it
  const avatarUrl = reactor.avatarUrl || ownerAvatarPath({owner: reactor.login})

  return (
    <li className={styles.Reactor}>
      <GitHubAvatar src={avatarUrl} size={20} alt="" />
      <div className={styles.ReactorIdentity}>
        <span className={styles.ReactorLogin}>{reactor.login}</span>
        {reactor.displayName && reactor.displayName !== reactor.login && (
          <span className={styles.ReactorName}>{reactor.displayName}</span>
        )}
      </div>
    </li>
  )
}

function ReactionToggle({
  buttonRef,
  content,
  descriptionId,
  reactorsDescriptionId,
  label,
  onClick,
  totalCount,
  viewerHasReacted,
  disabled,
  hoverPopoverAnchorProps,
}: {
  buttonRef: React.Ref<HTMLButtonElement>
  content: ReactionContent
  descriptionId: string
  reactorsDescriptionId: string
  label: string
  onClick: () => void
  totalCount: number
  viewerHasReacted: boolean
  disabled?: boolean
  hoverPopoverAnchorProps?: {
    onClick: () => void
    onFocus: () => void
    onBlur: (event: React.FocusEvent<HTMLElement>) => void
  }
}) {
  return (
    <Button
      {...hoverPopoverAnchorProps}
      ref={buttonRef}
      size="small"
      aria-label={label}
      role="switch"
      aria-checked={viewerHasReacted}
      leadingVisual={() => <>{EMOJI_MAP[content]}</>}
      onClick={event => {
        if (event.detail !== 0) hoverPopoverAnchorProps?.onClick()
        onClick()
      }}
      inactive={disabled}
      className={clsx(styles.reactionToggleButton, viewerHasReacted && styles.reactionToggleButtonReacted)}
      aria-describedby={reactorsDescriptionId ? `${descriptionId} ${reactorsDescriptionId}` : descriptionId}
    >
      <span className="sr-only" id={descriptionId}>
        {constructReactionButtonDescription(content, viewerHasReacted)}
      </span>
      {totalCount}
    </Button>
  )
}

export const ReactionButton = memo(function ReactionButtonComponent({
  reactionGroup,
  disabled,
  showReactors = false,
  onReact,
}: ReactionButtonProps) {
  const {content, viewerHasReacted} = reactionGroup.reaction
  const {reactors, totalCount} = reactionGroup
  const descriptionId = useId()
  const hovercardId = useId()
  const reactorsDescriptionId = useId()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const onFirstOpen = useCallback(
    () => sendAnalyticsEvent('reaction_hovercard.shown', undefined, {content}),
    [content, sendAnalyticsEvent],
  )
  const {open, anchorRegionProps, anchorProps, contentProps, closeNow} = useHoverPopover({onFirstOpen})

  if (totalCount === 0) return null

  const visibleReactors = reactors.filter(reactor => reactor.typeName !== 'Other' && reactor.login)
  const reactorNames = visibleReactors.map(reactor => reactor.displayName || reactor.login)
  const legacyReactorNames = visibleReactors.map(reactor => reactor.tooltipName || reactor.login)
  const label = constructReactionButtonLabel(content, totalCount)
  const hiddenReactorCount = Math.max(totalCount - visibleReactors.length, 0)

  if (showReactors && visibleReactors.length > 0) {
    return (
      <div {...anchorRegionProps} className={styles.ReactionPopoverAnchor}>
        <ReactionToggle
          buttonRef={buttonRef}
          content={content}
          descriptionId={descriptionId}
          reactorsDescriptionId={reactorsDescriptionId}
          label={label}
          onClick={() => onReact(content, viewerHasReacted)}
          totalCount={totalCount}
          viewerHasReacted={viewerHasReacted}
          disabled={disabled}
          hoverPopoverAnchorProps={anchorProps}
        />
        <span className="sr-only" id={reactorsDescriptionId}>
          Reacted by {summarizeReactors(reactorNames, totalCount)}
        </span>
        {open && (
          <AnchoredOverlay
            open={open}
            anchorRef={buttonRef}
            renderAnchor={null}
            onClose={closeNow}
            displayCloseButton={false}
            width="auto"
            className={styles.ReactorOverlay}
            preventOverflow={false}
            focusTrapSettings={{disabled: true}}
            focusZoneSettings={{disabled: true}}
            side="outside-top"
            align="start"
            overlayProps={{
              ...contentProps,
              role: 'group',
              'aria-label': `People who reacted with ${EMOJI_MAP[content]}`,
              tabIndex: 0,
              // Without this, Primer focuses the overlay on open, leaving a stray
              // focus ring for pointer-driven (hover) opens.
              preventFocusOnOpen: true,
            }}
          >
            <div id={hovercardId}>
              {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Preserve semantics in Safari/VoiceOver when CSS removes list markers. */}
              <ul className={styles.ReactorList} role="list">
                {visibleReactors.map(reactor => (
                  <ReactorListItem key={`${reactor.typeName}-${reactor.login}`} reactor={reactor} />
                ))}
              </ul>
              {hiddenReactorCount > 0 && (
                <div className={styles.MoreReactors}>
                  {`+${hiddenReactorCount} more ${hiddenReactorCount === 1 ? 'person' : 'people'}`}
                </div>
              )}
            </div>
          </AnchoredOverlay>
        )}
      </div>
    )
  }

  return (
    <Tooltip text={summarizeReactors(legacyReactorNames, totalCount)} type="description" direction="ne">
      <Button
        size="small"
        aria-label={label}
        role="switch"
        aria-checked={viewerHasReacted}
        leadingVisual={() => <>{EMOJI_MAP[content]}</>}
        onClick={() => onReact(content, viewerHasReacted)}
        inactive={disabled}
        className={clsx(styles.reactionToggleButton, viewerHasReacted && styles.reactionToggleButtonReacted)}
        aria-describedby={descriptionId}
      >
        <span className="sr-only" id={descriptionId}>
          {constructReactionButtonDescription(content, viewerHasReacted)}
        </span>
        {totalCount}
      </Button>
    </Tooltip>
  )
})
